import { APP_INITIALIZER, NgModule, Optional, SkipSelf } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { switchMap, tap } from 'rxjs/operators';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { BsDatepickerConfig, BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { BsModalService, ModalModule } from 'ngx-bootstrap/modal';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { TimepickerModule } from 'ngx-bootstrap/timepicker';
import { KeycloakService } from 'keycloak-angular';
import { ThgcLayoutModule } from '@thg-procure-team/procure-common-ui/platform/layout';
import {
  ThgcLocalizationModule,
  ThgcLocalizationService,
} from '@thg-procure-team/procure-common-ui/platform/localization';
import { ThgcConfirmModule } from '@thg-procure-team/procure-common-ui/platform/confirm';
import { ThgcToastModule } from '@thg-procure-team/procure-common-ui/platform/toast';
import { ThgcConfigModule, ThgcConfigService } from '@thg-procure-team/procure-common-ui/platform/config';
import { UserService } from '@thg-procure-team/procure-common-ui/business/user';
import { MaintenanceModule } from '@thg-procure-team/procure-common-ui/platform/maintenance';
import { ThgcRouterResolveProgressbarModule } from '@thg-procure-team/procure-common-ui/platform/router-resolve-progressbar';

import { LayoutHomeComponent } from '@procure-warehouse/core/layouts/layout-home/layout-home.component';
import { LayoutNotFoundComponent } from '@procure-warehouse/core/layouts/layout-not-found/layout-not-found.component';
import { HttpModule } from '@procure-warehouse/core/http/http.module';

@NgModule({
  declarations: [LayoutHomeComponent, LayoutNotFoundComponent],
  imports: [
    CommonModule,
    HttpModule,
    RouterModule,
    BsDropdownModule,
    TooltipModule,
    BsDatepickerModule,
    PaginationModule,
    ModalModule,
    TimepickerModule,
    PopoverModule,
    ThgcLayoutModule,
    ThgcConfirmModule,
    ThgcToastModule,
    ThgcLocalizationModule,
    ThgcConfigModule,
    ThgcRouterResolveProgressbarModule,
    MaintenanceModule,
  ],
  providers: [
    KeycloakService,
    HttpModule,
    {
      provide: APP_INITIALIZER,
      useFactory: (configService: ThgcConfigService) => () =>
        configService
          .load()
          .pipe(
            tap((config) => {
              const meta = document.createElement('meta');
              meta.httpEquiv = 'Content-Security-Policy';
              meta.content = `default-src 'self'; style-src 'self' 'unsafe-inline'; img-src 'self' data:; connect-src ${config.cspConnectSrc}`;
              document.head.prepend(meta);
            }),
          )
          .toPromise(),
      multi: true,
      deps: [ThgcConfigService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (
        userService: UserService,
        thgcConfigService: ThgcConfigService,
        thgcLocalizationService: ThgcLocalizationService,
      ) => () =>
        thgcConfigService
          .load()
          .pipe(
            tap((config) => (thgcLocalizationService.availableLocales = config.availableLanguages)),
            switchMap((config) => userService.login(config.keyCloakConfig)),
          )
          .toPromise(),
      multi: true,
      deps: [UserService, ThgcConfigService, ThgcLocalizationService],
    },
    {
      provide: BsDatepickerConfig,
      useFactory: (thgcLocalizationService: ThgcLocalizationService) => ({
        ...new BsDatepickerConfig(),
        showWeekNumbers: false,
        dateInputFormat: thgcLocalizationService.translate('shared.dateFormat').toUpperCase(),
      }),
      deps: [ThgcLocalizationService],
    },
  ],
  exports: [
    BrowserAnimationsModule,
    HttpModule,
    LayoutHomeComponent,
    ThgcToastModule,
    ModalModule,
    MaintenanceModule,
    ThgcConfigModule,
    ThgcRouterResolveProgressbarModule,
  ],
})
export class CoreModule {
  constructor(
    @Optional() @SkipSelf() parentCoreModule: CoreModule,
    private readonly modalService: BsModalService,
  ) {
    if (parentCoreModule) {
      throw new Error(
        `${parentCoreModule.constructor.name} has already been loaded. Import this module in the AppModule only.`,
      );
    }
    this.modalService.config.class = 'modal-lg';
    this.modalService.config.ignoreBackdropClick = true;
    this.modalService.config.keyboard = false;
  }
}
